import React, { useEffect, ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Image, Button, Stack } from 'react-bootstrap';
import { map, isUndefined, isNull } from 'lodash';
import { useTranslation } from 'react-i18next';

import { BackButton, ClaimBadge, Loading, LoadingError, NoData } from '@components/Base';
import { PaymentMethods } from '@models/PaymentInfo';
import { ClaimableTypes } from '@models/Claim';
import { getPersonalAccidentClaimType } from '@models/PersonalAccidentClaim';
import { amountToUsd } from '@utils/currency';
import { formatDateTime } from '@utils/datetime';
import { getAttachmentType, isAttachmentImage, isAttachmentPdf } from '@utils/attachment';

import { selectPersonalAccidentClaim } from './selector';
import { personalAccidentClaimFetch } from './action';
import { useEditable } from '../hook';
import { ClaimEditModal } from '../../ClaimEditModal';

interface RowItemProps {
  label: string;
  value?: string | number | ReactElement | null;
  list?: string[];
  renderItem?: (item: string) => void;
}

const RowItem = ({ label, value, list, renderItem }: RowItemProps) => {
  return (
    <Row className="mb-2">
      <Col xs={4} sm={4} md={3}>
        {label}
      </Col>
      <Col xs="auto">:</Col>
      <Col>
        <>
          {value && value}
          {list && renderItem && map(list, renderItem)}
        </>
      </Col>
    </Row>
  );
};

interface ClaimAttachmentItemProps {
  name: string;
  url: string;
}

const ClaimAttachmentItem = ({ name, url }: ClaimAttachmentItemProps) => {
  const { t } = useTranslation();
  const attachmentType = getAttachmentType(url);

  return (
    <>
      {isAttachmentImage(attachmentType) && (
        <Image src={url} thumbnail className="mb-3" data-testid={`${name}-image`} />
      )}

      {isAttachmentPdf(attachmentType) && (
        <Link to={url} className="d-block mb-3" target="_blank" rel="noreferrer" data-testid={`${name}-pdf`}>
          {t('common:clickToViewDetails')}
        </Link>
      )}
    </>
  );
};

const PersonalAccidentClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableType, claimableId } = useParams();
  const { personalAccidentClaim } = useSelector(selectPersonalAccidentClaim);
  const isLoaded = !isUndefined(personalAccidentClaim);
  const isError = isNull(personalAccidentClaim);
  const hasPersonalAccidentClaim = isLoaded && !isNull(personalAccidentClaim);
  const shouldEdit = useEditable(personalAccidentClaim);
  const isPaymentByBankTransfer = personalAccidentClaim?.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReload = () => {
    dispatch(personalAccidentClaimFetch({ id: Number(claimableId) }));
  };

  useEffect(() => {
    dispatch(personalAccidentClaimFetch({ id: Number(claimableId) }));
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasPersonalAccidentClaim && <NoData />}

      {personalAccidentClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              {shouldEdit && (
                <Button size="sm" onClick={() => setIsModalOpen(true)}>
                  {t('common:edit')}
                </Button>
              )}
            </Stack>
          </Card.Header>
          <Card.Body>
            <RowItem label={t('common:status')} value={<ClaimBadge status={personalAccidentClaim.status} />} />
            <RowItem label={t('common:claimNumber')} value={personalAccidentClaim.claimNumber} />
            <RowItem
              label={t('personalAccidentInsuranceClaimForm.accidentHappenedAt.label')}
              value={formatDateTime(personalAccidentClaim.accidentHappenedAt)}
            />
            <RowItem
              label={t('claimForm.claimType.label')}
              value={getPersonalAccidentClaimType(personalAccidentClaim.claimType)}
            />
            <RowItem
              label={t('claimForm.supportingDocuments.label')}
              list={personalAccidentClaim.supportingDocuments}
              renderItem={(url) => <ClaimAttachmentItem name="supporting-documents" url={url} />}
            />
            <RowItem
              label={t('common:additionalDocuments')}
              list={personalAccidentClaim.additionalDocuments}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="additional-documents" url={url} />}
            />
            <RowItem
              label={t('claimForm.totalClaimedAmount.label')}
              value={amountToUsd(personalAccidentClaim.totalClaimedAmount)}
            />
            <RowItem
              label={t('claimForm.approvedAmount.label')}
              value={amountToUsd(personalAccidentClaim.approvedAmount)}
            />
            <RowItem label={t('common:note')} value={personalAccidentClaim.note} />
            <hr />
            <RowItem label={t('paymentInfo.paymentMethod')} value={personalAccidentClaim.paymentInfo?.paymentMethod} />
            <RowItem
              label={t('paymentInfo.beneficiaryName')}
              value={personalAccidentClaim.paymentInfo?.beneficiaryName}
            />
            {isPaymentByBankTransfer && (
              <>
                <RowItem label={t('paymentInfo.bankName')} value={personalAccidentClaim.paymentInfo?.bankName} />
                <RowItem label={t('paymentInfo.bankAccount')} value={personalAccidentClaim.paymentInfo?.bankAccount} />
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {personalAccidentClaim && (
        <ClaimEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          claimNumber={personalAccidentClaim.claimNumber}
          claimableType={ClaimableTypes.PersonalAccident}
          claimableId={personalAccidentClaim.id}
          currentStatus={personalAccidentClaim.status}
          currentTotalClaimedAmount={personalAccidentClaim.totalClaimedAmount}
          onSuccess={handleReload}
        />
      )}
    </>
  );
};

export { PersonalAccidentClaimDetails };
